export interface SkinSpacing {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

type AxisValue = number | string | undefined;

export interface SkinComponentLayoutLike {
  id?: string;
  type: "box" | "text" | "image" | "icon" | "collection-list" | "tabs" | "dropdown";
  parentId?: string;
  margin?: SkinSpacing;
  position: {
    left?: number | string;
    right?: number | string;
    top?: number | string;
    bottom?: number | string;
  };
  size: {
    width: number | string;
    height: number | string;
    minWidth?: number | string;
    minHeight?: number | string;
    maxWidth?: number | string;
    maxHeight?: number | string;
    aspectRatio?: string;
  };
  style: Record<string, unknown>;
}

export interface ResolvedComponentLayoutStyle {
  minWidth?: string;
  minHeight?: string;
  maxWidth?: string;
  maxHeight?: string;
  left?: string;
  right?: string;
  top?: string;
  bottom?: string;
  width?: string;
  height?: string;
}

interface ParentComponentLike {
  id: string;
  style?: Record<string, unknown>;
}

const isPercentageValue = (value: unknown): value is string =>
  typeof value === "string" && value.endsWith("%");

const parseNumber = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    return Number.isNaN(value) ? undefined : value;
  }

  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  return undefined;
};

const toSizeCss = (value: number | string | undefined): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === "number") return `${value}px`;
  return value;
};

const toInsetCss = (value: AxisValue, offset = 0): string | undefined => {
  if (value === undefined) return undefined;

  if (isPercentageValue(value)) {
    return offset !== 0 ? `calc(${value} + ${offset}px)` : value;
  }

  const parsed = parseNumber(value);
  if (parsed === undefined) {
    return typeof value === "string" ? value : undefined;
  }

  return `${parsed + offset}px`;
};

const calculateWidth = (component: SkinComponentLayoutLike): string | undefined => {
  if (component.type === "box" || component.type === "tabs") {
    if (typeof component.size.width === "string" && component.size.width.endsWith("%")) {
      return component.size.width;
    }

    if (typeof component.size.width === "number") {
      return `${component.size.width}px`;
    }

    if (component.size.width === "auto") {
      return "100%";
    }

    if (component.size.aspectRatio) {
      return component.size.width;
    }

    if (component.position.left !== undefined && component.position.right !== undefined) {
      if (component.size.width !== "auto") {
        return component.size.width;
      }
      return undefined;
    }

    if (component.position.left !== undefined && component.position.right === undefined) {
      if (typeof component.position.left === "string" && component.position.left.endsWith("%")) {
        const leftPercent = parseFloat(component.position.left);
        if (!Number.isNaN(leftPercent)) {
          return `calc(100% - ${leftPercent}%)`;
        }
      } else {
        const leftValue = parseNumber(component.position.left);
        if (leftValue !== undefined) {
          return `calc(100% - ${leftValue}px)`;
        }
      }
    }

    if (component.position.right !== undefined && component.position.left === undefined) {
      return component.size.width === "auto" ? "auto" : toSizeCss(component.size.width);
    }

    return "100%";
  }

  if (component.type === "text") {
    if (typeof component.size.width === "string" && component.size.width.endsWith("%")) {
      return component.size.width;
    }

    if (typeof component.size.width === "number") {
      return `${component.size.width}px`;
    }

    if (component.size.width === "100%") {
      return "100%";
    }

    if (component.size.width === "auto") {
      return "auto";
    }

    if (component.position.left !== undefined && component.position.right !== undefined) {
      if (component.size.width !== "auto") {
        return component.size.width;
      }
      return undefined;
    }

    if (component.position.left !== undefined && component.position.right === undefined) {
      const leftValue = parseNumber(component.position.left);
      if (leftValue !== undefined) {
        return `calc(100% - ${leftValue}px)`;
      }
    }

    if (component.position.right !== undefined && component.position.left === undefined) {
      return component.size.width === "auto" ? "auto" : toSizeCss(component.size.width);
    }

    return "100%";
  }

  if (component.position.left !== undefined && component.position.right !== undefined) {
    const leftValue = parseNumber(component.position.left);
    const rightValue = parseNumber(component.position.right);
    if (leftValue !== undefined && rightValue !== undefined) {
      return `calc(100% - ${leftValue}px - ${rightValue}px)`;
    }
  }

  if (component.position.left !== undefined && component.size.width === "100%") {
    if (typeof component.position.left === "string" && component.position.left.endsWith("%")) {
      const leftPercent = parseFloat(component.position.left);
      if (!Number.isNaN(leftPercent)) {
        return `calc(100% - ${leftPercent}%)`;
      }
    } else {
      const leftValue = parseNumber(component.position.left);
      if (leftValue !== undefined) {
        return `calc(100% - ${leftValue}px)`;
      }
    }
  }

  if (component.position.right !== undefined && component.size.width === "100%") {
    if (typeof component.position.right === "string" && component.position.right.endsWith("%")) {
      const rightPercent = parseFloat(component.position.right);
      if (!Number.isNaN(rightPercent)) {
        return `calc(100% - ${rightPercent}%)`;
      }
    } else {
      const rightValue = parseNumber(component.position.right);
      if (rightValue !== undefined) {
        return `calc(100% - ${rightValue}px)`;
      }
    }
  }

  return toSizeCss(component.size.width);
};

const calculateHeight = (component: SkinComponentLayoutLike): string | undefined => {
  if (component.size.aspectRatio) {
    return "auto";
  }

  if (component.position.top !== undefined && component.position.bottom !== undefined) {
    const topValue = parseNumber(component.position.top);
    const bottomValue = parseNumber(component.position.bottom);
    if (topValue !== undefined && bottomValue !== undefined) {
      return `calc(100% - ${topValue}px - ${bottomValue}px)`;
    }
  }

  if (
    component.position.top !== undefined &&
    (component.size.height === "auto" || component.size.height === "100%")
  ) {
    if (typeof component.position.top === "string" && component.position.top.endsWith("%")) {
      const topPercent = parseFloat(component.position.top);
      if (!Number.isNaN(topPercent)) {
        return `calc(100% - ${topPercent}%)`;
      }
    } else {
      const topValue = parseNumber(component.position.top);
      if (topValue !== undefined) {
        return `calc(100% - ${topValue}px)`;
      }
    }
  }

  if (
    component.position.top !== undefined &&
    component.position.bottom !== undefined &&
    (component.size.height === "auto" || component.size.height === "100%")
  ) {
    if (typeof component.position.bottom === "string" && component.position.bottom.endsWith("%")) {
      const bottomPercent = parseFloat(component.position.bottom);
      if (!Number.isNaN(bottomPercent)) {
        return `calc(100% - ${bottomPercent}%)`;
      }
    } else {
      const bottomValue = parseNumber(component.position.bottom);
      if (bottomValue !== undefined) {
        return `calc(100% - ${bottomValue}px)`;
      }
    }
  }

  return toSizeCss(component.size.height);
};

export const resolveComponentLayoutStyle = (
  component: SkinComponentLayoutLike,
  parentPadding: SkinSpacing = { top: 0, right: 0, bottom: 0, left: 0 },
): ResolvedComponentLayoutStyle => {
  const margin = component.margin || { top: 0, right: 0, bottom: 0, left: 0 };
  const leftOffset =
    component.position.left !== undefined
      ? margin.left
      : component.position.right !== undefined
        ? -margin.left
        : 0;
  const rightOffset =
    component.position.right !== undefined
      ? margin.right
      : component.position.left !== undefined
        ? -margin.right
        : 0;
  const topOffset =
    component.position.top !== undefined
      ? margin.top
      : component.position.bottom !== undefined
        ? -margin.top
        : 0;
  const bottomOffset =
    component.position.bottom !== undefined
      ? margin.bottom
      : component.position.top !== undefined
        ? -margin.bottom
        : 0;

  return {
    minWidth: toSizeCss(component.size.minWidth),
    minHeight: toSizeCss(component.size.minHeight),
    maxWidth: toSizeCss(component.size.maxWidth),
    maxHeight: toSizeCss(component.size.maxHeight),
    left:
      component.position.left !== undefined
        ? toInsetCss(component.position.left, parentPadding.left + leftOffset)
        : component.parentId && component.position.right === undefined
          ? `${parentPadding.left + leftOffset}px`
          : undefined,
    right:
      component.position.right !== undefined
        ? toInsetCss(component.position.right, rightOffset)
        : component.parentId &&
            component.type !== "text" &&
            component.position.left === undefined
          ? `${parentPadding.right + rightOffset}px`
          : undefined,
    top:
      component.position.top !== undefined
        ? toInsetCss(component.position.top, parentPadding.top + topOffset)
        : component.parentId && component.position.bottom === undefined
          ? `${parentPadding.top + topOffset}px`
          : undefined,
    bottom:
      component.position.bottom !== undefined
        ? toInsetCss(component.position.bottom, bottomOffset)
        : component.parentId &&
            component.type !== "text" &&
            component.position.top === undefined
          ? `${parentPadding.bottom + bottomOffset}px`
          : undefined,
    width: calculateWidth(component),
    height: calculateHeight(component),
  };
};

export const getParentPaddingForComponent = (
  parentId: string | undefined,
  components: ParentComponentLike[],
): SkinSpacing => {
  if (!parentId) return { top: 0, right: 0, bottom: 0, left: 0 };

  const parentComponent = components.find((component) => component.id === parentId);
  if (!parentComponent) return { top: 0, right: 0, bottom: 0, left: 0 };

  return {
    top: parseNumber(parentComponent.style?.paddingTop) ?? 0,
    right: parseNumber(parentComponent.style?.paddingRight) ?? 0,
    bottom: parseNumber(parentComponent.style?.paddingBottom) ?? 0,
    left: parseNumber(parentComponent.style?.paddingLeft) ?? 0,
  };
};
